import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";

export type AddressPurpose = "domicile" | "mailing" | "situs";

export interface CanonicalAddress {
  street1: string;
  street2: string | null;
  city: string | null;
  state: string | null;
  zip: string;
  zip4: string | null;
  county: string | null;
}

interface AddressRow {
  street1: string | null;
  street2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  zip4: string | null;
  county: string | null;
  validation_status: string | null;
}

function toCanonical(row: AddressRow | null, includePartial: boolean): CanonicalAddress | null {
  const isShown =
    row?.validation_status === "valid" || (includePartial && row?.validation_status === "partial");
  return row?.street1 && row.zip && isShown
    ? {
        street1: row.street1,
        street2: row.street2,
        city: row.city,
        state: row.state,
        zip: row.zip,
        zip4: row.zip4,
        county: row.county,
      }
    : null;
}

const toValidCanonical = (row: AddressRow | null) => toCanonical(row, false);
const toShownCanonical = (row: AddressRow | null) => toCanonical(row, true);

type EntityColumn = "estate_id" | "contact_id" | "estate_item_id" | "user_id";

async function fetchAddressRow(
  column: EntityColumn,
  entityId: number,
  purpose: AddressPurpose,
): Promise<AddressRow | null> {
  const { data, error } = await supabase
    .from("entity_addresses")
    .select("addresses(street1, street2, city, state, zip, zip4, county, validation_status)")
    .eq(column, entityId)
    .eq("purpose", purpose)
    .is("valid_to", null)
    .order("valid_from", { ascending: false, nullsFirst: false })
    .order("id", { ascending: false })
    .limit(1);

  if (error) throw error;

  const joined = (data ?? [])[0]?.addresses as AddressRow | AddressRow[] | null | undefined;
  return (Array.isArray(joined) ? joined[0] : joined) ?? null;
}

export function useCanonicalAddress(
  estateId: number | undefined,
  purpose: AddressPurpose,
  { includePartial = false }: { includePartial?: boolean } = {},
) {
  return useQuery({
    queryKey: ["canonicalAddress", "estate_id", estateId, purpose],
    queryFn: () => fetchAddressRow("estate_id", estateId!, purpose),
    select: includePartial ? toShownCanonical : toValidCanonical,
    enabled: !!estateId,
    staleTime: 60_000,
  });
}

export function useCanonicalUserAddress(userId: number | undefined) {
  return useQuery({
    queryKey: ["canonicalAddress", "user_id", userId, "mailing"],
    queryFn: () => fetchAddressRow("user_id", userId!, "mailing"),
    select: toValidCanonical,
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function canonicalItemAddressKey(itemId: number | undefined) {
  return ["canonicalAddress", "estate_item_id", itemId, "situs"] as const;
}

export function useCanonicalItemAddress(itemId: number | undefined) {
  return useQuery({
    queryKey: canonicalItemAddressKey(itemId),
    queryFn: () => fetchAddressRow("estate_item_id", itemId!, "situs"),
    select: toValidCanonical,
    enabled: !!itemId,
    staleTime: 60_000,
  });
}
